import { FloatingPortal, offset, size, useFloating, useDismiss, useInteractions } from "@floating-ui/react"
import { Transition, Dialog } from "@headlessui/react"
import { useSSRDisabled } from "@skyltmax/ds9/hooks"
import cn from "classnames"
import { motion, AnimatePresence } from "framer-motion"
import { t } from "i18next"
import React, { useState, useCallback, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import videojs from "video.js"
import type Player from "video.js/dist/types/player"
import { type Sign as ExampleSign } from "@/api/graphql"
import Checkbox from "@/common/components/checkbox"
import Scrollable from "@/common/components/scrollable"
import env from "@/common/env"
import useMedia from "@/common/hooks/use_media"
import { sizeUnit } from "@/common/locales"
import { type SizeApplyArgs } from "@/common/util"
import { mergeRefs } from "@/common/util/react"
import Icons from "@/images/signomatic/fixmethods"
import { useMinimaticData, useMinimaticLoaded } from "@/signomatic/app/minimatic"
import { type SignStateData } from "@/signomatic/app/sign"
import type Sign from "@/signomatic/app/sign"
import { description, reason } from "@/signomatic/components/fixmethod_toolbox"
import SelectedIndicator from "@/signomatic/components/selected_indicator"
import { ArrowLeftLarge, ArrowRightLarge, LockClosedFill, ChevronDown, CloseLarge, CheckmarkLarge } from "@/skylticons"

interface FixmethodMedia {
  key: string
  image?: string | null
  video?: string | null
  text?: string | null
}

interface SignMediaObject {
  key: string
  fixmethods: FixmethodMedia[]
}

type FixmethodsMedia = SignMediaObject[]

interface MediaModalProps {
  closeModal: () => void
  open: boolean
  src: string
}

const MediaModal: React.FC<MediaModalProps> = ({ open, closeModal, src }) => {
  const { t } = useTranslation()
  const videoRef = useRef(null)
  const image = src.endsWith(".png")
  const video = src.endsWith(".mp4")
  const text = src.includes("<h5>")

  useEffect(() => {
    if (!video) {
      return
    }

    let player: Player
    if (videoRef.current && open) {
      player = videojs(videoRef.current, {
        controls: true,
        preload: "auto",
        fluid: true,
      })
    }

    return () => {
      if (player) {
        player.dispose()
      }
    }
  }, [open, video])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeModal()
        return
      }
    }

    document.addEventListener("keydown", handler)

    return () => document.removeEventListener("keydown", handler)
  }, [closeModal])

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  }

  return (
    <AnimatePresence>
      {open && (
        <Dialog static open={open} onClose={() => null}>
          <motion.div
            className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={closeModal}
          >
            {(image || video || text) && (
              <motion.div
                className={cn("grid h-auto w-[1024px] items-end px-3 lg:px-0", { "w-auto": text || image })}
                variants={overlayVariants}
                onClick={e => e.stopPropagation()}
              >
                <button type="button" onClick={closeModal} className="button button-secondary !ml-auto mb-2">
                  <CloseLarge />
                  {t("misc.close")}
                </button>
                {image && <img className="rounded-3xl" src={src} alt="" />}
                {video && (
                  <div className="video-wrapper h-full w-full">
                    <video
                      ref={videoRef}
                      className="video-js fixmethod-video vjs-default-skin h-full w-full overflow-hidden rounded-3xl"
                    >
                      <source src={src} type="video/mp4" />
                    </video>
                  </div>
                )}
                {text && <div className="rounded-lg bg-white-5 p-6" dangerouslySetInnerHTML={{ __html: src }} />}
              </motion.div>
            )}
          </motion.div>
        </Dialog>
      )}
    </AnimatePresence>
  )
}

interface OptionProps {
  label: string
  selected: boolean
  onSelected: () => void
  scrollIntoView: boolean
  setScrolledIntoView: (value: boolean) => void
  signType: string
  material: string
  fixmethodMedia?: FixmethodMedia
  openMediaModal: (src: string) => void
  disabledReason?: string
}

const Option: React.FC<OptionProps> = ({
  label,
  selected,
  onSelected,
  scrollIntoView,
  setScrolledIntoView,
  signType,
  material,
  fixmethodMedia,
  openMediaModal,
  disabledReason,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const descriptionText = t(description(label, signType, material))
  const image = fixmethodMedia?.image || null
  const video = fixmethodMedia?.video || null
  const text = fixmethodMedia?.text || null
  const none = label === "none"

  useEffect(() => {
    if (scrollIntoView) {
      setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" }), 1)
      setScrolledIntoView(true)
    }
  }, [scrollIntoView, setScrolledIntoView])

  return (
    <div
      className={cn(
        `relative mb-0 flex h-auto w-full cursor-pointer scroll-mb-96 flex-col items-center justify-center border border-transparent p-4 text-base focus:border-primary lg:scroll-m-28 xl:min-h-[116px] fixmethod-${label}`,
        {
          "hover:bg-white-3": !selected,
          "bg-blue-100 hover:bg-blue-200": selected,
          "pointer-events-none": !!disabledReason,
        }
      )}
      onClick={onSelected}
      ref={ref}
    >
      {!!disabledReason && (
        <div className={cn("disabled-reason mb-2 self-start text-xs font-semibold text-red-500", disabledReason)}>
          {t(reason(disabledReason))}
        </div>
      )}
      <div className={cn("flex w-full items-center", { "opacity-50": !!disabledReason })}>
        <div className="image mr-4 flex h-12 w-12">{Icons[label] ? React.createElement(Icons[label]) : ""}</div>
        <div className={cn("flex", { "w-full flex-col": !none, "w-full justify-between": none })}>
          <h5 className="heading-five mb-0">{t(`hang_type.${label}`)}</h5>
          <p className="paragraph-2xs mb-1">{descriptionText}</p>
          {descriptionText ? (
            <>
              {["pole", "pole_flanged"].includes(label) && (
                <p className="paragraph-2xs">
                  <br />
                  {env.locale == "en-GB" ? "(Ø 76 mm)" : `(Ø ${sizeUnit === "in" ? "2-2.5" : "50–65"} ${sizeUnit}) `}
                </p>
              )}
              {image || video || text ? (
                <div className="info-row flex justify-between">
                  <p className="paragraph-xs mb-0">
                    <a
                      onClick={e => {
                        e.stopPropagation()
                        openMediaModal(video || image || text || "")
                      }}
                      className="underline"
                    >
                      {video && t("toolbox.see_video")}
                      {image && t("toolbox.see_image")}
                      {text && t("misc.readmore")}
                      ...
                    </a>
                  </p>
                  <SelectedIndicator active={selected} />
                </div>
              ) : (
                <div className="info-row right">
                  <SelectedIndicator active={selected} />
                </div>
              )}
            </>
          ) : (
            <SelectedIndicator active={selected} />
          )}
        </div>
      </div>
    </div>
  )
}

interface SelectProps {
  id: string
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean | undefined>>
  selected: string
  selectedLabel: (value: string) => React.ReactNode
  label: string
  children: React.ReactNode
  optionsCount: number
  modalOpen: boolean
}

export const Select = ({
  id,
  open,
  setOpen,
  selected,
  selectedLabel,
  label,
  children,
  optionsCount,
  modalOpen,
}: SelectProps) => {
  const { t } = useTranslation()
  const desktop = useMedia(`(min-width: 1024px)`, true)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const handleOpen = (open: boolean) => {
    if (modalOpen) {
      return
    }

    setOpen(open)

    if (!open) {
      buttonRef.current?.focus()
    }
  }

  const toggle = () => handleOpen(!open)

  const { x, y, strategy, refs, context } = useFloating({
    open,
    onOpenChange: desktop ? handleOpen : undefined,
    middleware: desktop
      ? [
          offset(2),
          size({
            apply({ availableWidth, elements, rects }: SizeApplyArgs) {
              Object.assign(elements.floating.style, {
                maxWidth: `${availableWidth}px`,
                width: `${rects.reference.width}px`,
              })
            },
          }),
        ]
      : [],
  })

  const dismiss = useDismiss(context)

  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss])

  const ssrDisabled = useSSRDisabled()

  const dropdownContents = <>{children}</>

  // mobile
  const mt = 144
  const availableHeight = window.innerHeight - mt
  const headerAndFooter = 270
  const optionsHeight = optionsCount * 100
  const dropdownHeight = optionsHeight + headerAndFooter

  const scrollable = desktop ? optionsCount > 6 : availableHeight < dropdownHeight

  return (
    <div className="relative">
      <label htmlFor={id} className="w-full">
        {label && <div className="mb-1 text-sm font-semibold">{label}</div>}
        <div className="relative w-full">
          <button
            onClick={toggle}
            id={id}
            aria-invalid="false"
            ref={mergeRefs(buttonRef, refs.setReference)}
            type="button"
            disabled={ssrDisabled}
            className={cn(
              "fixmethod-dropdown inline-flex h-12 w-full items-center truncate rounded border pl-4 pr-12 text-base outline-none transition placeholder:text-[rgb(117,117,117)] focus:border-primary focus:ring-2 focus:ring-primary focus:ring-opacity-50 lg:h-10",
              {
                "hover:border-ink-50": !open,
                "cursor-not-allowed bg-gray-50": ssrDisabled,
              }
            )}
            {...getReferenceProps()}
          >
            <>
              <div className="w-full truncate">
                {selectedLabel ? selectedLabel(selected) : <>{typeof selected === "string" ? selected : ""}</>}
              </div>
              <div className="absolute inset-y-0 right-3 flex items-center justify-center text-ink-100">
                <ChevronDown className={cn("h-6 w-6 transition-transform", { "rotate-180": open })} />
              </div>
            </>
          </button>
        </div>
      </label>

      <FloatingPortal>
        {desktop ? (
          open && (
            <div
              className="minimatic-fixmethod-dropdown z-20 flex w-full origin-top flex-col rounded-lg border border-white-10 bg-white shadow-custom-sm focus:outline-none lg:min-h-[84px]"
              ref={refs.setFloating}
              style={{
                position: strategy,
                top: y ?? 0,
                left: x ?? 0,
                width: "max-content",
              }}
              {...getFloatingProps()}
            >
              {scrollable ? (
                <div className="visible-scrollbar mt-2 h-96 flex-grow border-white-10">
                  <Scrollable>{dropdownContents}</Scrollable>
                </div>
              ) : (
                <div className="mt-2 border-white-10">{dropdownContents}</div>
              )}
            </div>
          )
        ) : (
          <Transition show={open}>
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-150"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 z-30 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 z-30 overflow-y-auto">
              <div className="flex min-h-full items-start justify-center px-3 py-4 text-center sm:items-center sm:p-0">
                <Transition.Child
                  as={React.Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-10"
                  enterTo="opacity-100 translate-y-0"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-10"
                >
                  <div
                    className={cn(
                      "minimatic-fixmethod-mobile-dropdown fixed inset-x-0 bottom-0 z-40 mt-36 flex transform flex-col rounded-t-2xl bg-white shadow-custom-sm ring-0 transition-all focus:outline-none",
                      {
                        "top-0": scrollable,
                      }
                    )}
                  >
                    <div className="flex flex-shrink-0 flex-grow-0 items-center justify-between gap-3 p-4">
                      <h4 className="heading-four mb-0">{t("examples.valj_typsnitt")}</h4>
                      <button
                        onClick={toggle}
                        className="flex h-12 w-12 flex-shrink-0 items-center justify-center"
                        type="button"
                      >
                        <CloseLarge className="h-6 w-6" />
                      </button>
                    </div>
                    {scrollable ? (
                      <div className="visible-scrollbar mt-2 flex-grow border-t border-white-10">
                        <Scrollable>{dropdownContents}</Scrollable>
                      </div>
                    ) : (
                      <div className="mt-2 border-t border-white-10">{dropdownContents}</div>
                    )}
                    <button
                      type="button"
                      className="button button-primary button-medium m-4 !ml-4 mt-2"
                      onClick={toggle}
                    >
                      <CheckmarkLarge />
                      <span>{t("toolbox.done")}</span>
                    </button>
                  </div>
                </Transition.Child>
              </div>
            </div>
          </Transition>
        )}
      </FloatingPortal>
    </div>
  )
}

export interface MinimaticFixmethodsConfiguration {
  fixmethods?: string[]
  fixmethodsEnabled?: boolean
}

export type MinimaticFixmethodsSign = Pick<ExampleSign, "fixmethod">

interface Fixmethod {
  name: string
  disabledReason?: string
}

type handlerFunc = (
  minimatic: Sign
) => [handler: (data: SignStateData) => void, current: string | undefined, fixmethods: Fixmethod[]]

const useFixmethodHandler: handlerFunc = minimatic => {
  const [availableFixmethods, setFixmethods] = useState<Fixmethod[]>([])
  const [current, setCurrent] = useState<string>()

  const handler = useCallback(
    ({ state, data }: SignStateData) => {
      const signFixmethod = state.fixmethod

      setCurrent(signFixmethod)

      const currentData = data.find(data => data.name === minimatic.signType)

      if (currentData) {
        setFixmethods(
          currentData.fixmethods
            .filter(
              fm =>
                fm.enabled &&
                fm.name !== "bockad" &&
                (fm?.filters?.fixed_size
                  ? fm.filters.fixed_size.w === state.width && fm.filters.fixed_size.h === state.height
                  : true) &&
                !fm?.filters?.admin
            )
            .map(fm => ({ name: fm.name, disabledReason: (fm.disabled_reason && fm.disabled_reason[0]) || "" }))
        )
      }
    },
    [minimatic.signType]
  )

  return [handler, current, availableFixmethods]
}

interface MinimaticFixmethodsConfigurerProps {
  minimatic: Sign
  config?: MinimaticFixmethodsConfiguration
  update: (config: Partial<MinimaticFixmethodsConfiguration>) => void
}

export const MinimaticFixmethodsConfigurer: React.FC<MinimaticFixmethodsConfigurerProps> = ({
  minimatic,
  config,
  update,
}) => {
  const loaded = useMinimaticLoaded(minimatic)
  const [handler, current, availableFixmethods] = useFixmethodHandler(minimatic)
  useMinimaticData(handler, minimatic)
  const [availableFixmethodsSelected, setAvailableFixmethodsSelected] = useState<string[]>([])
  const [activeFixmethodsSelected, setActiveFixmethodsSelected] = useState<string[]>([])
  const activeFixmethods = config?.fixmethods || availableFixmethods.filter(f => f.name !== current).map(f => f.name)
  const availableFixmethodsFiltered = availableFixmethods
    .filter(f => !activeFixmethods.includes(f.name) && f.name !== current)
    .map(f => f.name)
  const active = typeof config?.fixmethodsEnabled !== "undefined" ? config.fixmethodsEnabled : false

  useEffect(() => {
    if (config?.fixmethods && config.fixmethods.length > 0 && config.fixmethods.includes(current || "")) {
      update({ fixmethods: [...availableFixmethods.filter(f => f.name !== current).map(f => f.name)] })
    }
  }, [availableFixmethods, config?.fixmethods, current, update])

  if (!current || !loaded) {
    return null
  }

  const toggleFixmethod = (fixmethod: string) => {
    setAvailableFixmethodsSelected(selected => {
      if (selected.find(f => f === fixmethod)) {
        return selected.filter(f => f !== fixmethod)
      } else {
        return [...selected, fixmethod]
      }
    })
  }

  const toggleActiveFixmethod = (fixmethod: string) => {
    setActiveFixmethodsSelected(selected => {
      if (selected.find(f => f === fixmethod)) {
        return selected.filter(f => f !== fixmethod)
      } else {
        return [...selected, fixmethod]
      }
    })
  }

  const moveToActive = () => {
    setAvailableFixmethodsSelected([])
    update({ fixmethods: [...activeFixmethods, ...availableFixmethodsSelected] })
  }

  const moveToAvailable = () => {
    setActiveFixmethodsSelected([])
    update({ fixmethods: activeFixmethods.filter(f => !activeFixmethodsSelected.find(a => a === f)) })
  }

  const toggleFixmethodsConfig = (checked: boolean) => {
    update({ fixmethodsEnabled: checked })

    if (checked) {
      update({ fixmethods: activeFixmethods.filter(f => !activeFixmethodsSelected.find(a => a === f)) })
    }
  }

  return (
    <div className="minimatic-fixmethods-config grid grid-cols-[1fr_min-content_1fr]" data-title="fixmethods">
      <div className="mb-3 flex items-center gap-2">
        <h4 className="mb-0 w-auto whitespace-nowrap text-xl leading-6">All fixing methods</h4>
        <p className="paragraph-sm mb-0 leading-6">Remove fixing methods that aren&apos;t relevant</p>
      </div>
      <div></div>
      <div className="mb-3 flex items-center gap-3 [&_.checkbox-label]:!mb-0 [&_.checkbox]:mb-0 [&_.checkbox]:py-0">
        <h4 className="mb-0 w-auto whitespace-nowrap text-xl leading-6">Active fixing methods</h4>
        <Checkbox
          className="toggle_fixmethods"
          name="toggle_fixmethods"
          checked={active}
          label="Activate"
          onChange={e => toggleFixmethodsConfig(e.target.checked)}
        />
      </div>
      <div
        className={cn("minimatic-fixmethods-available h-72 rounded-lg border border-white-20 py-4", {
          "config-disabled pointer-events-none bg-white-5 opacity-50": !active,
        })}
      >
        <FixmethodList
          fixmethods={availableFixmethodsFiltered}
          active={availableFixmethodsSelected}
          onSelect={toggleFixmethod}
        />
      </div>
      <div className="flex items-center">
        <div className="space-y-2 p-1">
          <div>
            <button
              className="minimatic-activate-fixmethods button button-secondary icon-only mb-0"
              type="button"
              disabled={availableFixmethodsSelected.length === 0}
              onClick={moveToActive}
            >
              <ArrowRightLarge className="h-6 w-6" />
            </button>
          </div>
          <div>
            <button
              className="minimatic-deactivate-fixmethods button button-secondary icon-only mb-0"
              type="button"
              disabled={activeFixmethodsSelected.length === 0}
              onClick={moveToAvailable}
            >
              <ArrowLeftLarge className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>
      <div
        className={cn("minimatic-fixmethods-selected h-72 rounded-lg border border-white-20 p-4", {
          "pointer-events-none bg-white-5 opacity-50": !active,
        })}
      >
        <FixmethodList
          fixmethods={[current, ...activeFixmethods]}
          active={activeFixmethodsSelected}
          locked={current}
          onSelect={toggleActiveFixmethod}
        />
      </div>
    </div>
  )
}

interface FixmethodListProps {
  fixmethods: string[]
  active: Array<string | undefined>
  locked?: string
  onSelect: (fixmethods: string) => void
}

const FixmethodList = ({ fixmethods, active, locked, onSelect }: FixmethodListProps) => {
  return (
    <div className="flex h-full flex-wrap gap-2">
      <Scrollable>
        {fixmethods.map(fixmethod => {
          const lockedFixmethod = locked == fixmethod

          return (
            <div
              key={fixmethod.toString()}
              className={cn(
                "minimatic-fixmethod relative flex h-10 cursor-pointer items-center rounded transition",
                `fixmethod-${fixmethod}`,
                {
                  "mx-3 pl-4": !locked,
                  "bg-blue-100 hover:bg-blue-200": active.includes(fixmethod),
                  "hover:bg-white-3": !active.includes(fixmethod),
                  "pointer-events-none opacity-90": lockedFixmethod,
                }
              )}
              onClick={() => onSelect(fixmethod)}
            >
              {locked && (
                <div className="mr-2 w-6">
                  {lockedFixmethod && <LockClosedFill className="h-6 w-6 [&_path]:stroke-white-3 [&_path]:stroke-1" />}
                </div>
              )}

              <div className="image mr-2 flex h-8 w-8">
                {Icons[fixmethod] ? React.createElement(Icons[fixmethod]) : ""}
              </div>
              <span className="paragraph paragraph-sm mb-0">
                {t(`hang_type.${fixmethod}`)} ({fixmethod})
              </span>
            </div>
          )
        })}
      </Scrollable>
    </div>
  )
}

interface MinimaticFixmethodsProps {
  minimatic?: Sign
  config?: MinimaticFixmethodsConfiguration | null
  loadingFailed?: boolean
  setFixmethod: (fixmethod: string) => void
  fixmethodsMedia?: FixmethodsMedia
  setMinimaticSlide: () => void
}

export const MinimaticFixmethods: React.FC<MinimaticFixmethodsProps> = ({
  minimatic,
  config,
  loadingFailed,
  setFixmethod,
  fixmethodsMedia,
  setMinimaticSlide,
}) => {
  const loaded = useMinimaticLoaded(minimatic)

  if (config && !loadingFailed && (!minimatic || !loaded) && (config.fixmethods || []).length >= 1) {
    return <FixmethodPickerSkeleton />
  }

  if (!minimatic || !loaded || !config) {
    return null
  }

  return (
    <FixmethodPicker
      minimatic={minimatic}
      config={config}
      setFixmethod={setFixmethod}
      fixmethodsMedia={fixmethodsMedia || []}
      setMinimaticSlide={setMinimaticSlide}
    />
  )
}

const FixmethodPickerSkeleton: React.FC = () => {
  const { t } = useTranslation()

  return (
    <div className="mb-4">
      <div className="relative">
        <label className="w-full">
          <div className="mb-1 text-sm font-semibold">{t("toolbox.fix_method")}</div>
          <div className="relative h-10 w-full overflow-hidden rounded">
            <div className="skeleton skeleton-rounded h-10 w-full"></div>
          </div>
        </label>
      </div>
    </div>
  )
}

interface FixmethodPickerProps {
  minimatic: Sign
  config: MinimaticFixmethodsConfiguration
  setFixmethod: (fixmethod: string) => void
  fixmethodsMedia: FixmethodsMedia
  setMinimaticSlide: () => void
}

const FixmethodPicker: React.FC<FixmethodPickerProps> = ({
  minimatic,
  config,
  setFixmethod,
  fixmethodsMedia,
  setMinimaticSlide,
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [source, setSource] = useState("")
  const [scrolledIntoView, setScrolledIntoView] = useState(false)
  const [initFixmethod, setInitFixmethod] = useState<string>()
  const [configFixmethods, setConfigFixmethods] = useState<string[]>()
  const [handler, current, availableFixmethods] = useFixmethodHandler(minimatic)
  useMinimaticData(handler, minimatic)
  const currentFixmethod = minimatic.state.fixmethod

  useEffect(() => {
    if (!initFixmethod) {
      setInitFixmethod(minimatic.state.fixmethod)
    }
  }, [initFixmethod, minimatic.state.fixmethod])

  useEffect(() => {
    setConfigFixmethods(config?.fixmethods || [])

    if (availableFixmethods.length > 0) {
      setConfigFixmethods(prevFixmethods => {
        if (config?.fixmethods?.length === 0) {
          return []
        } else if (prevFixmethods?.includes(initFixmethod || "")) {
          return availableFixmethods.filter(f => f.name !== initFixmethod).map(f => f.name)
        }
        return prevFixmethods
      })
    }
  }, [availableFixmethods, config?.fixmethods, initFixmethod])

  const handleOpen = (open: boolean) => {
    setOpen(open)
    setScrolledIntoView(false)
  }

  const initfixmethodOptions = [initFixmethod || "", ...(configFixmethods || [])]
  const fixmethodOptions = [...new Set(initfixmethodOptions)]

  const onChange = (fixmethod: string) => {
    minimatic.updateState({ fixmethod: fixmethod })
    setMinimaticSlide()
    setFixmethod(fixmethod)
  }

  const openMediaModal = (src: string) => {
    setSource(src)
    setModalOpen(true)
  }

  if (!config.fixmethodsEnabled || (configFixmethods || []).length < 1) {
    return null
  }

  return (
    <div className="mb-4">
      <Select
        id="example-fixmethod-select"
        label={t("toolbox.fix_method")}
        open={open}
        setOpen={handleOpen}
        selected={current || minimatic.state.fixmethod || ""}
        selectedLabel={v => (
          <div className="flex w-full items-center justify-between">
            <span>{v ? t(`hang_type.${v}`) : ""}</span>
            <div className="flex items-center gap-2"></div>
          </div>
        )}
        optionsCount={fixmethodOptions.length}
        modalOpen={modalOpen}
      >
        {fixmethodOptions.map((fixmethodOpt, i) => {
          const fixmethodMedia = fixmethodsMedia
            .find(sign => sign.key === minimatic.signType)
            ?.fixmethods.find(fixmethod => fixmethod.key === fixmethodOpt)

          return (
            <Option
              key={i}
              label={fixmethodOpt}
              selected={fixmethodOpt === currentFixmethod}
              onSelected={() => onChange(fixmethodOpt)}
              scrollIntoView={open && fixmethodOpt === currentFixmethod && !scrolledIntoView}
              setScrolledIntoView={setScrolledIntoView}
              signType={minimatic.signType || ""}
              material={minimatic.state.material}
              fixmethodMedia={fixmethodMedia}
              openMediaModal={openMediaModal}
              disabledReason={availableFixmethods.find(fm => fm.name === fixmethodOpt)?.disabledReason}
            ></Option>
          )
        })}
      </Select>
      <MediaModal closeModal={() => setModalOpen(false)} open={modalOpen} src={source} />
    </div>
  )
}
